// packages/common-ui/src/utils/analyticsDetection.ts
import { homepageRulesEngine } from './analyticsRulesEngine';

export interface HomepageAnalyticsResult {
  sectionName: string;
  eventType: 'homeHeroBanner' | 'homeSectionBanner';
  detectionMethod: 'cms-explicit' | 'rules-engine' | 'pattern-matching';
  confidence: 'high' | 'medium' | 'low';
  metadata?: {
    layoutPosition?: number;
    originalTitle?: string;
    ruleId?: string;
  };
}

export type AnalyticsSectionName =
  | 'hero banner'
  | 'best selling'
  | 'categories'
  | 'clubs'
  | 'deals'
  | 'trending'
  | 'unlimited'
  | 'support'
  | 'sms'
  | 'social'
  | 'unknown';

export type AnalyticsEventType = 'homeHeroBanner' | 'homeSectionBanner';

export type DetectionMethod = 'cms-explicit' | 'rules-engine' | 'pattern-matching';

/**
 * Detects section name from content title using pattern matching (fallback strategy)
 * @param entryTitle - The content entry title from CMS
 * @returns The detected section name or 'unknown' if no patterns match
 */
const detectSectionNameFromTitle = (entryTitle?: string): AnalyticsSectionName => {
  if (!entryTitle) {
    return 'unknown';
  }

  const normalizedTitle = entryTitle.toLowerCase();

  // Define pattern mappings for section names
  const patternMappings: { section: AnalyticsSectionName; patterns: string[] }[] = [
    { section: 'hero banner', patterns: ['hero'] },
    { section: 'best selling', patterns: ['best-selling', 'best selling'] },
    { section: 'categories', patterns: ['featured categories'] },
    { section: 'clubs', patterns: ['clubs'] },
    { section: 'deals', patterns: ['offers', 'deals'] },
    { section: 'trending', patterns: ['trending'] },
    { section: 'unlimited', patterns: ['unlimited'] },
    { section: 'support', patterns: ['customer service', 'support'] },
    { section: 'sms', patterns: ['sms'] },
    { section: 'social', patterns: ['social'] },
  ];

  for (const { section, patterns } of patternMappings) {
    if (patterns.some(pattern => normalizedTitle.includes(pattern))) {
      return section;
    }
  }

  // Return unknown for unmatched patterns
  return 'unknown';
};

/**
 * Determines the analytics section and event type for homepage content using hybrid detection strategies
 * @param params - Object containing contentItem, layoutIndex, and componentType
 * @param params.contentItem - Contentful content item with fields
 * @param params.layoutIndex - Position of the content in the homepage layout (0-based index)
 * @param params.componentType - The component type identifier
 * @returns Structured analytics data with section name, event type, and detection metadata
 */
export const detectHomepageAnalytics = ({
  contentItem,
  layoutIndex,
  componentType,
}: {
  contentItem: Record<string, unknown>;
  layoutIndex?: number;
  componentType?: string;
}): HomepageAnalyticsResult | null => {
  if (!contentItem) {
    return null;
  }

  const contentFields = contentItem?.fields as Record<string, unknown>;
  const entryTitle = contentFields?.entryTitle as string;

  // Strategy 1: CMS Explicit Configuration (highest confidence)
  if (contentFields?.analyticsSection) {
    return {
      sectionName: contentFields.analyticsSection as AnalyticsSectionName,
      eventType: (contentFields.analyticsEventType as AnalyticsEventType) || 'homeSectionBanner',
      detectionMethod: 'cms-explicit',
      confidence: 'high',
      metadata: {
        layoutPosition: layoutIndex,
        originalTitle: entryTitle,
      },
    };
  }

  // Strategy 2: Rules Engine Detection (medium-high confidence)
  const rulesEngineResult = homepageRulesEngine.analyzeContent({
    title: entryTitle,
    componentType,
    layoutIndex,
    contentItem,
  });
  if (rulesEngineResult) {
    return {
      sectionName: rulesEngineResult.section as AnalyticsSectionName,
      eventType: rulesEngineResult.eventType,
      detectionMethod: 'rules-engine',
      confidence: 'high',
      metadata: {
        layoutPosition: layoutIndex,
        originalTitle: entryTitle,
        ruleId: rulesEngineResult.ruleId,
      },
    };
  }

  // Strategy 3: Pattern Matching Fallback (lower confidence)
  const detectedSectionName = detectSectionNameFromTitle(entryTitle);
  return {
    sectionName: detectedSectionName,
    eventType: detectedSectionName === 'hero banner' ? 'homeHeroBanner' : 'homeSectionBanner',
    detectionMethod: 'pattern-matching',
    confidence: 'low',
    metadata: {
      layoutPosition: layoutIndex,
      originalTitle: entryTitle,
    },
  };
};
